import React from "react";
import { AlertCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";

import { Money, MoneyText } from "@workspace/accounting/components";
import type {
	Currency,
	Denomination,
	Money as MoneyType,
} from "@workspace/accounting/types";
import { HttpRequestError } from "@workspace/base-api/fetchJson";
import { CheckOperation } from "@workspace/operations/check-operation";
import { genid } from "@workspace/shared/utils";
import { TerminalPayment } from "@workspace/stripe";
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import { Separator } from "@workspace/ui/components/separator";
import { PageLoading, useVenueSettings } from "@workspace/venue-management";

import type { Order, Payment } from "../../types";
import { compedItemIds, compReasonsByItem } from "../comp";
import type { OpenOrderData, PaymentMethods } from "../epos-context";
import { useEposContext } from "../epos-context";
import { isAwaitingCardConfirmation } from "../epos-context/card-payment-recovery";
import { optionRequiredOf } from "../option-required";
import { taxBreakdown } from "../order-tax";
import { AddTip } from "./add-tip";
import { PaymentOrderLines } from "./payment-order-lines";
import { PrintReceiptButton } from "./print-receipt-button";
import { SplitPayment } from "./split-payment";

function AppliedPayments({ order }: { order: Order }) {
	// HA-886 — the label is by the payment's age, so the clock has to move.
	const [now, setNow] = React.useState(() => Date.now());
	React.useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 5_000);
		return () => clearInterval(timer);
	}, []);

	const completedPayments = order.payments.filter(
		(p) => p.status === "Completed" || p.status === "Pending",
	);

	if (completedPayments.length === 0) return null;

	return (
		<div className="border-b p-4 space-y-2">
			<h4 className="text-sm font-medium">Applied Payments</h4>
			{completedPayments.map((p) => (
				<div key={p.id} className="flex items-center justify-between text-sm">
					<div className="flex items-center gap-2">
						<span className="capitalize">{p.method}</span>
						{p.cardDetails?.last4 && (
							<span className="text-muted-foreground">
								****{p.cardDetails.last4}
							</span>
						)}
						{p.externalId && !p.cardDetails?.last4 && (
							<span className="text-muted-foreground text-xs">
								{p.externalId.slice(-8)}
							</span>
						)}
						{/* HA-886 — held against the bill, but the server has not confirmed it was taken. */}
						{isAwaitingCardConfirmation(p, now) && (
							<span className="text-xs text-amber-600">
								Awaiting card confirmation
							</span>
						)}
					</div>
					<div className="flex items-center gap-2">
						{p.tip && p.tip.value > 0 && (
							<span className="text-xs text-muted-foreground">
								(tip: <Money {...p.tip} />)
							</span>
						)}
						<Money {...p.amount} />
					</div>
				</div>
			))}
			{order.outstanding && order.outstanding.value > 0 && (
				<div className="flex justify-between text-sm font-medium pt-1 border-t">
					<span>Remaining</span>
					<Money {...order.outstanding} />
				</div>
			)}
		</div>
	);
}

function PaymentHeader({ order: currentOrder }: { order: Order }) {
	return (
		<div className="border-b p-4">
			<h3 className="text-lg font-semibold">Payment</h3>
			<div className="mt-2 flex items-center justify-between">
				<div>
					<span className="text-sm font-medium">Order ID:</span>{" "}
					<span className="text-sm text-muted-foreground">
						{currentOrder.id}
					</span>
				</div>
				<div>
					<span className="text-sm font-medium">To Pay:</span>{" "}
					<Money {...currentOrder.outstanding} />
				</div>
				<div>
					<span className="text-sm font-medium">Paid:</span>{" "}
					<Money {...currentOrder.totalPaid} />
				</div>
				<div>
					<span className="text-sm font-medium">Change:</span>{" "}
					<Money {...currentOrder.change} />
				</div>
			</div>
		</div>
	);
}

function Receipt() {
	const {
		data,
		currentOrder,
		operations,
		currencies: {
			formatter: { format: formatCurrency },
		},
	} = useEposContext();

	if (!currentOrder) return null;

	const taxParts = taxBreakdown(currentOrder);

	return (
		<div className="space-y-4 py-4">
			<div className="rounded-md border p-4 font-mono text-sm">
				<div className="mb-4 text-center">
					{data.settings.receiptHeader.split("\n").map((line, i) => (
						// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: display-only text lines, which can repeat and have no identity
						<div key={i}>{line}</div>
					))}
				</div>

				<div className="mb-2 text-center">
					<div>Order #{currentOrder.id}</div>
					<div>
						{new Date(
							currentOrder.completedAt || currentOrder.updatedAt,
						).toLocaleString()}
					</div>
					<div>Server: {currentOrder.server.personalDetails.preferredName}</div>
				</div>

				<Separator className="my-2" />

				<PaymentOrderLines
					items={currentOrder.items}
					compedItemIds={compedItemIds(currentOrder)}
					compReasons={compReasonsByItem(currentOrder)}
				/>

				<Separator className="my-2" />

				<div className="space-y-1">
					<div className="flex justify-between">
						<div>Subtotal</div>
						<div>
							<Money {...currentOrder.subtotal} />
						</div>
					</div>

					{/* HA-657: prices include VAT, so the contained VAT is never labelled "Tax" (which reads as added on
					    top); an order rung before that, or an untaxed one, keeps its Tax row as before. */}
					{(taxParts.onTop > 0 || taxParts.included === 0) && (
						<div className="flex justify-between">
							<div>Tax ({data.settings.taxRate}%)</div>
							<div>
								{formatCurrency(
									taxParts.onTop > 0 ? taxParts.onTop : currentOrder.tax,
								)}
							</div>
						</div>
					)}

					{currentOrder.tip && currentOrder.tip > 0 && (
						<div className="flex justify-between">
							<div>Tip</div>
							<div>{formatCurrency(currentOrder.tip)}</div>
						</div>
					)}

					<div className="flex justify-between font-bold">
						<div>Total</div>
						<div>
							<Money
								value={currentOrder.total.value + (currentOrder.tip || 0)}
								currency={currentOrder.total.currency}
							/>
						</div>
					</div>

					{taxParts.included > 0 && (
						<div className="flex justify-between text-sm text-muted-foreground">
							<div>Includes VAT</div>
							<div>{formatCurrency(taxParts.included)}</div>
						</div>
					)}
				</div>

				{currentOrder.payments.length > 0 && (
					<>
						<Separator className="my-2" />

						<div className="space-y-1">
							<div className="font-bold">Payment Details</div>
							{currentOrder.payments.map((p) => (
								<div key={p.id} className="flex justify-between">
									<div className="capitalize">
										{p.method}
										{p.tip && p.tip.value > 0 && (
											<span className="text-xs">
												{" "}
												(tip: {formatCurrency(p.tip.value)})
											</span>
										)}
									</div>
									<div>
										<Money {...p.amount} />
									</div>
								</div>
							))}
							{currentOrder.change && currentOrder.change.value > 0 && (
								<div className="flex justify-between">
									<div>Change</div>
									<div>
										<Money {...currentOrder.change} />
									</div>
								</div>
							)}
						</div>
					</>
				)}

				<Separator className="my-2" />

				<div className="mt-4 text-center text-xs">
					{data.settings.receiptFooter.split("\n").map((line, i) => (
						// eslint-disable-next-line @eslint-react/no-array-index-key -- HA-743: display-only text lines, which can repeat and have no identity
						<div key={i}>{line}</div>
					))}
				</div>
			</div>

			<div className="flex justify-center gap-2">
				<CheckOperation
					operation="staff-terminal-print-receipt"
					operations={operations}
				>
					<PrintReceiptButton orderId={currentOrder.id} />
				</CheckOperation>
			</div>
		</div>
	);
}

export function PaymentView() {
	const {
		currentOrder,
		openOrderData,

		setActiveOrderId,
		setSplitMethod,

		defaultCurrency,

		payCash,
		cancelPayment,
		currencies,
		startCardPayment,
		isStartingCard,
		paymentMethod,

		paymentProvider,
		data,
		tipAmount,
		setTipAmount,
		showOptionRefusal,
	} = useEposContext();

	const [showSplitPayment, setShowSplitPayment] = React.useState(false);

	// HA-849 — whole-order cash is recorded on the server before the till shows it paid, one tender
	// at a time (a second press while one is in flight would be a different tender under its id).
	const [isRecordingCash, setIsRecordingCash] = React.useState(false);
	const handleCashPayment = async (payment: Payment) => {
		if (isRecordingCash) return;
		setIsRecordingCash(true);
		try {
			await payCash(payment);
		} catch (error) {
			// HA-783 — Retry sends THIS tender again, same id and amount, so the server recognises one
			// that did land (its answer lost) instead of recording it twice.
			// HA-877: a line still needs a required choice. Retrying the tender can't help; go back to the order,
			// where the line's editor opens with the message inside it (HA-1024).
			const missing = optionRequiredOf(error);
			if (missing) {
				showOptionRefusal(payment.orderId, missing);
				cancelPayment();
				return;
			}

			const problem =
				error instanceof HttpRequestError ? error.problem?.detail : undefined;
			toast.error("The cash payment couldn't be recorded", {
				description:
					problem ?? (error instanceof Error ? error.message : "Unknown error"),
				action: {
					label: "Retry",
					onClick: () => void handleCashPayment(payment),
				},
			});
		} finally {
			setIsRecordingCash(false);
		}
	};

	function handleCancel() {
		cancelPayment();
	}

	function handleSplitPaymentBack() {
		setShowSplitPayment(false);
		setActiveOrderId(currentOrder?.id || null);
		setSplitMethod("none");
	}

	if (!currentOrder || !openOrderData) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-muted-foreground">
					No active order. Please select a staff member or table.
				</p>
			</div>
		);
	}

	const methods =
		(paymentProvider.paymentMethods as PaymentMethods[] | null) || [];

	if (showSplitPayment) {
		return (
			<div className="flex h-full flex-col">
				<div className="flex-1 overflow-auto">
					<SplitPayment />
				</div>

				{/* Footer: Cancel must stay at bottom of parent regardless of scroll */}
				<div className="border-t p-4 flex space-x-2">
					<Button
						variant="outline"
						className="w-full"
						onClick={() => handleSplitPaymentBack()}
					>
						Back
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col">
			<PaymentHeader order={currentOrder} />
			<AppliedPayments order={currentOrder} />

			<div className="flex-1 overflow-auto">
				{openOrderData.status !== "Completed" ? (
					<>
						{methods.includes("card") && paymentMethod === "card" ? (
							<CardPayment />
						) : (
							<>
								{/* HA-858 (S1, AC4): a manual tip rides on the next tender; none while the venue takes no tips. */}
								<AddTip
									enabled={data.tipsEnabled}
									owed={currentOrder.outstanding ?? currencies.money(0)}
									tip={tipAmount}
									onChange={setTipAmount}
								/>
								<div className="border-b p-4">
									<Button
										variant="outline"
										className="w-full"
										onClick={() => setShowSplitPayment(true)}
									>
										Split Payment
									</Button>
								</div>
								{methods.includes("cash") && (
									// HA-357: Exact tenders what the bill's "To Pay" shows (the server's figures, with the
									// service charge), not the order's snapshot, which can still be the pre-charge amount.
									<CashPaymentOptions
										order={openOrderData}
										exactAmount={currentOrder.outstanding ?? undefined}
										zero={currencies.money(0)}
										onClick={(denomination) =>
											void handleCashPayment(denomination)
										}
										disabled={isRecordingCash}
										currency={defaultCurrency}
										denominations={defaultCurrency.denominations?.filter(
											(x) => x.active,
										)}
									/>
								)}

								{methods.includes("card") && (
									<div className="border-b p-4">
										<h4 className="mb-2 text-sm font-medium">Card</h4>
										<Button
											variant="default"
											className="h-12 w-full"
											disabled={isStartingCard}
											onClick={() => startCardPayment(currentOrder)}
										>
											<CreditCard className="mr-2 size-5" />
											Pay with Card
										</Button>
									</div>
								)}
							</>
						)}
					</>
				) : (
					<Receipt />
				)}
			</div>

			{/* Footer: Cancel must stay at bottom of parent regardless of scroll */}
			{currentOrder.status !== "Completed" && (
				<div className="border-t p-4">
					<Button variant="outline" className="w-full" onClick={handleCancel}>
						Cancel
					</Button>
				</div>
			)}
		</div>
	);
}

export function CashPaymentOptions({
	denominations = [],
	currency,
	onClick,
	zero,
	order,
	disabled = false,
	exactAmount,
}: {
	denominations: Denomination[] | undefined;
	currency?: Currency;
	onClick?: (payment: Payment) => void;
	zero: MoneyType;
	order: OpenOrderData;
	disabled?: boolean;
	/** HA-849 — what Exact tenders; the order's outstanding when not given. */
	exactAmount?: MoneyType;
}) {
	const [cashPayments, setCashPayments] = React.useState<Denomination[]>([]);
	const paymentIdRef = React.useRef(genid());

	// Reset accumulator when totalPaid changes (i.e. after a partial payment is processed)
	const prevTotalPaid = React.useRef(order.totalPaid?.value ?? 0);
	React.useEffect(() => {
		const currentTotalPaid = order.totalPaid?.value ?? 0;
		if (currentTotalPaid !== prevTotalPaid.current) {
			prevTotalPaid.current = currentTotalPaid;
			setCashPayments([]);
			paymentIdRef.current = genid();
		}
	}, [order.totalPaid?.value]);

	React.useEffect(() => {
		if (cashPayments.length === 0) return;
		const totalCash = cashPayments.reduce((acc, p) => acc + p.value, 0);

		const p: Payment = {
			id: paymentIdRef.current,
			method: "cash",
			status: "Pending",
			orderId: order.id,
			amount: {
				...zero,
				value: totalCash,
			},
			timestamp: new Date().toISOString(),
		};

		onClick?.(p);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cashPayments]);

	const outstandingAmount = exactAmount ?? order.outstanding ?? order.total;

	return (
		<div className="border-b p-4 space-y-4">
			<h4 className="mb-2 text-sm font-medium">Cash</h4>
			<div className="grid grid-cols-3 gap-2">
				{denominations.map((denomination) => (
					<Button
						key={denomination.name}
						variant="outline"
						className="h-12"
						disabled={disabled}
						onClick={() => setCashPayments((p) => [...p, denomination])}
					>
						<MoneyText {...denomination} currency={currency?.code} />
					</Button>
				))}

				<Button
					variant="outline"
					className="h-12"
					disabled={disabled}
					onClick={() =>
						onClick?.({
							id: paymentIdRef.current,
							method: "cash",
							status: "Pending",
							orderId: order.id,
							amount: outstandingAmount,
							timestamp: new Date().toISOString(),
						})
					}
				>
					Exact
				</Button>
			</div>
		</div>
	);
}

export function CardPayment() {
	const { tenant } = useVenueSettings();
	const {
		cardPayments,
		currentOrder,
		processPayment,
		setPaymentMethod,
		data: { terminals },
		completeOrder,
		tipAmount,
	} = useEposContext();

	if (!currentOrder) return null;

	if (cardPayments.status === "Pending" || cardPayments.status === "None") {
		return (
			<div className="justify-center h-full">
				<PageLoading text="Starting payment..." />
			</div>
		);
	}

	if (cardPayments.status === "Failed") {
		return (
			<div className="flex h-full items-center justify-center p-4">
				<Alert
					variant="destructive"
					className="lg:w-1/2"
					role="alert"
					aria-live="assertive"
				>
					<AlertCircle />

					<AlertTitle
						id="no-menu-title"
						className="scroll-m-20 text-2xl font-semibold tracking-tight"
					>
						Error
					</AlertTitle>

					<AlertDescription id="no-menu-desc">
						{cardPayments.errorMessage || (
							<>An error occured while trying to take a card payment</>
						)}
					</AlertDescription>

					<div className="mt-4 flex justify-end col-span-2">
						{/* <Link
							to="/$venueId"
							params={{ venueId }}
							aria-label={`Back to venue ${venueId}`}
						>
							Back
						</Link> */}
					</div>
				</Alert>
			</div>
		);
	}

	return (
		<>
			{cardPayments.status === "Initiated" && (
				<TerminalPayment
					terminals={terminals}
					amount={cardPayments.amount?.value || 0}
					currency={currentOrder.currency || "USD"}
					orderId={currentOrder.id || ""}
					onProcessingChange={cardPayments.setProcessing}
					onCancelReady={cardPayments.setReaderCancel}
					onSuccess={async (paymentIntentId: string) => {
						// HA-767 — the card shows as paid only once the server has recorded it. This
						// awaited a call that resolved on any status, then completed the order without
						// waiting and navigated, so a refused or dropped request left a charged card on an
						// order that stayed Open.
						try {
							await cardPayments.success(paymentIntentId);
						} catch {
							toast.error("The card payment couldn't be recorded", {
								description:
									"The card may have been charged. Don't take the payment again — check the order first.",
							});
							return;
						}

						cardPayments.reset();

						// HA-858 (S2): the card was for the bill and any tip entered on the till; processPayment adds that
						// tip back as the payment's own, so this is the bill.
						const payment: Payment = {
							id: cardPayments.paymentId!,
							externalId: paymentIntentId,
							method: "card",
							status: "Completed",
							amount: {
								...cardPayments.amount!,
								value: cardPayments.amount!.value - tipAmount,
							},
							orderId: currentOrder.id,
							timestamp: new Date().toISOString(),
							paymentProviderInfo: "stripe",
						};

						processPayment("card", payment);

						// Check if order is now fully paid
						const totalPaidSoFar =
							currentOrder.payments.reduce(
								(acc, p) => acc + p.amount.value,
								0,
							) + payment.amount.value;
						const isFullyPaid = totalPaidSoFar >= currentOrder.total.value;

						if (isFullyPaid) {
							// completeOrder navigates once the server has completed the order. If it
							// fails, the card payment is recorded and the order stays here, Paid, with
							// Complete to try again.
							try {
								await completeOrder({
									...currentOrder,
									payments: [...currentOrder.payments, payment],
								});
							} catch (error) {
								toast.error(
									"The payment was recorded, but the order couldn't be completed",
									{
										description:
											error instanceof Error
												? `${error.message}. Press Complete to try again.`
												: "Press Complete to try again.",
									},
								);
							}
						} else {
							// Return to tender selection for next payment
							setPaymentMethod("cash");
						}
					}}
					onError={(error: Error) => {
						cardPayments.setError(error);
					}}
					tenant={tenant}
					clientSecret={cardPayments.clientSecret}
					skipTipping={cardPayments.skipTipping}
				/>
			)}

			{cardPayments.status === "Succeeded" && (
				<p className="text-green-600 font-medium">Card payment succeeded!</p>
			)}
			{cardPayments.status === "Failed" && (
				<p className="text-red-600 font-medium">Card payment failed.</p>
			)}
		</>
	);
}
